import React, { useCallback, useMemo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, Chip } from '@material-ui/core'
import SydLabel from '../../../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../../../commonDesign/SydSelect'
import SydInput from '../../../../../../../commonDesign/SydInput'
import SydButton from '../../../../../../../commonDesign/Button'
import IconPickerGrid from './IconPickerGrid'

const sizeOptions = [
  { label: 'Small', value: 'sm' },
  { label: 'Medium', value: 'md' },
  { label: 'Large', value: 'lg' },
  { label: 'Extra Large', value: 'xl' }
]

const alignOptions = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' }
]

const iconTypeOptions = [
  { label: 'Icon Library', value: 'library' },
  { label: 'Image URL', value: 'url' }
]

function MetricConfigEditor ({ config, allPurposes, onUpdate }) {
  const [localConfig, setLocalConfig] = useState(config)
  const [newFormat, setNewFormat] = useState('')

  useEffect(() => {
    setLocalConfig(config)
  }, [config])

  useEffect(() => {
    if (JSON.stringify(localConfig) !== JSON.stringify(config)) {
      onUpdate(localConfig)
    }
  }, [localConfig, config, onUpdate])

  const purposeOptions = useMemo(() => {
    const purposes = Object.entries(allPurposes || {})
    return [
      { label: 'Select a purpose...', value: '__placeholder__' },
      ...purposes.map(([code, data]) => ({
        label: `${data.displayName} (${code})`,
        value: code
      }))
    ]
  }, [allPurposes])

  const accessorOptions = useMemo(() => {
    if (!localConfig.purposeCode || !allPurposes[localConfig.purposeCode]) {
      return [{ label: 'Select a purpose first', value: '__placeholder__' }]
    }
    const columns = allPurposes[localConfig.purposeCode].columns || []
    return [
      { label: 'Select a column...', value: '__placeholder__' },
      ...columns.map(col => ({
        label: col.name,
        value: col.name
      }))
    ]
  }, [localConfig.purposeCode, allPurposes])

  const handlePurposeChange = useCallback((purposeCode) => {
    setLocalConfig(prev => ({
      ...prev,
      purposeCode: purposeCode === '__placeholder__' ? null : (purposeCode || null),
      accessor: null
    }))
  }, [])

  const handleAddFormat = useCallback(() => {
    const trimmed = newFormat.trim()
    if (!trimmed) return
    if ((localConfig.additionalFormats || []).includes(trimmed)) {
      setNewFormat('')
      return
    }
    setLocalConfig(prev => ({
      ...prev,
      additionalFormats: [...(prev.additionalFormats || []), trimmed]
    }))
    setNewFormat('')
  }, [newFormat, localConfig.additionalFormats])

  const handleRemoveFormat = useCallback((formatToRemove) => {
    setLocalConfig(prev => ({
      ...prev,
      additionalFormats: (prev.additionalFormats || []).filter(f => f !== formatToRemove)
    }))
  }, [])

  const iconType = localConfig.icon?.type || 'library'
  const iconValue = localConfig.icon?.value || ''

  const handleIconTypeChange = useCallback((type) => {
    setLocalConfig(prev => ({
      ...prev,
      icon: type && type !== '__none__' ? { type, value: '' } : null
    }))
  }, [])

  const handleIconValueChange = useCallback((value) => {
    setLocalConfig(prev => ({
      ...prev,
      icon: prev.icon ? { ...prev.icon, value } : { type: 'library', value }
    }))
  }, [])

  const handleClearIcon = useCallback(() => {
    setLocalConfig(prev => ({ ...prev, icon: null }))
  }, [])

  return (
    <Grid container spacing={2}>
      {/* Purpose Selection */}
      <Grid item xs={12}>
        <SydLabel required label='Data Purpose' helperText='Select which purpose provides the metric value'>
          <SydSelect
            options={purposeOptions}
            value={localConfig.purposeCode || '__placeholder__'}
            onChange={(e) => handlePurposeChange(e.target.value)}
          />
        </SydLabel>
      </Grid>

      {/* Accessor */}
      <Grid item xs={12}>
        <SydLabel required label='Value Column' helperText='Column from the first result row to display as the metric'>
          <SydSelect
            options={accessorOptions}
            value={localConfig.accessor || '__placeholder__'}
            onChange={(e) => setLocalConfig(prev => ({ ...prev, accessor: e.target.value === '__placeholder__' ? null : e.target.value }))}
          />
        </SydLabel>
      </Grid>

      {/* Title */}
      <Grid item xs={12}>
        <SydLabel label='Title' helperText='Optional title displayed above the metric value'>
          <SydInput
            value={localConfig.title || ''}
            onChange={(e) => setLocalConfig(prev => ({ ...prev, title: e.target.value || null }))}
            placeholder='e.g., Total Assets Under Management'
          />
        </SydLabel>
      </Grid>

      {/* Primary Format */}
      <Grid item xs={12} md={6}>
        <SydLabel label='Primary Format' helperText='Format string applied to the value (e.g., marketValue, percentage, number)'>
          <SydInput
            value={localConfig.primaryFormat || ''}
            onChange={(e) => setLocalConfig(prev => ({ ...prev, primaryFormat: e.target.value || null }))}
            placeholder='marketValue'
          />
        </SydLabel>
      </Grid>

      {/* Size & Alignment */}
      <Grid item xs={6} md={3}>
        <SydLabel label='Size'>
          <SydSelect
            options={sizeOptions}
            value={localConfig.size || 'md'}
            onChange={(e) => setLocalConfig(prev => ({ ...prev, size: e.target.value }))}
          />
        </SydLabel>
      </Grid>
      <Grid item xs={6} md={3}>
        <SydLabel label='Alignment'>
          <SydSelect
            options={alignOptions}
            value={localConfig.align || 'left'}
            onChange={(e) => setLocalConfig(prev => ({ ...prev, align: e.target.value }))}
          />
        </SydLabel>
      </Grid>

      {/* Additional Formats */}
      <Grid item xs={12}>
        <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h4 style={{ margin: 0 }}>Additional Formats</h4>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
            Cycle through these when user clicks the metric
          </span>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
          <SydInput
            value={newFormat}
            onChange={(e) => setNewFormat(e.target.value)}
            placeholder='e.g., percentage'
            style={{ maxWidth: '240px' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAddFormat()
              }
            }}
          />
          <SydButton size='sm' variant='primary' onClick={handleAddFormat} disabled={!newFormat.trim()}>
            Add
          </SydButton>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {(localConfig.additionalFormats || []).map((fmt) => (
            <Chip
              key={fmt}
              label={fmt}
              onDelete={() => handleRemoveFormat(fmt)}
              variant='outlined'
              size='small'
            />
          ))}
          {(localConfig.additionalFormats || []).length === 0 && (
            <span style={{ color: 'var(--text-color-subtle)', fontSize: '0.875rem' }}>
              No additional formats — metric will show only the primary format
            </span>
          )}
        </div>
      </Grid>

      {/* Icon */}
      <Grid item xs={12}>
        <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h4 style={{ margin: 0 }}>Icon</h4>
          {localConfig.icon && (
            <SydButton size='sm' variant='ghost' onClick={handleClearIcon}>
              Remove Icon
            </SydButton>
          )}
        </div>
        <div style={{ marginBottom: '12px' }}>
          <SydSelect
            options={[{ label: '(No icon)', value: '__none__' }, ...iconTypeOptions]}
            value={localConfig.icon ? iconType : '__none__'}
            onChange={(e) => handleIconTypeChange(e.target.value)}
          />
        </div>

        {localConfig.icon && iconType === 'library' && (
          <IconPickerGrid
            value={iconValue}
            onSelect={(name) => handleIconValueChange(name)}
          />
        )}

        {localConfig.icon && iconType === 'url' && (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <SydInput
                value={iconValue}
                onChange={(e) => handleIconValueChange(e.target.value)}
                placeholder='https://example.com/icon.png'
              />
            </div>
            {iconValue && (
              <img
                key={iconValue}
                src={iconValue}
                alt='Icon preview'
                style={{ width: '48px', height: '48px', objectFit: 'contain', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius-sm)', padding: '4px' }}
                onError={(e) => { e.target.style.display = 'none' }}
              />
            )}
          </div>
        )}
      </Grid>

      {/* Footer */}
      <Grid item xs={12}>
        <div style={{ marginBottom: '8px' }}>
          <h4 style={{ margin: 0 }}>Footer</h4>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
            Optional text below the metric value. Use {'{{columnName}}'} to substitute values from the data row.
          </span>
        </div>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <SydLabel label='Template'>
              <SydInput
                value={localConfig.footer?.template || ''}
                onChange={(e) => {
                  const template = e.target.value
                  setLocalConfig(prev => ({
                    ...prev,
                    footer: template ? { template, format: prev.footer?.format || null } : null
                  }))
                }}
                placeholder='e.g., As of {{asOfDate}}'
              />
            </SydLabel>
          </Grid>
          <Grid item xs={12} md={4}>
            <SydLabel label='Value Format' helperText='Format for substituted values'>
              <SydInput
                value={localConfig.footer?.format || ''}
                onChange={(e) => {
                  const format = e.target.value || null
                  setLocalConfig(prev => ({
                    ...prev,
                    footer: prev.footer ? { ...prev.footer, format } : null
                  }))
                }}
                placeholder='date'
                disabled={!localConfig.footer?.template}
              />
            </SydLabel>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

MetricConfigEditor.propTypes = {
  config: PropTypes.object.isRequired,
  allPurposes: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired
}

export default MetricConfigEditor
